import { clsx } from "clsx";
import { X } from "lucide-react";
import React, {
  ChangeEventHandler,
  InputHTMLAttributes,
  MouseEventHandler,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { FieldError } from "react-hook-form";

import { twMerge } from "@/design-system/twMerge";

import { extractFormErrorMessage } from "@/utils/form";
import { IconButton } from "@/design-system/components/IconButton/IconButton";

type CommonInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "id"> & {
  description?: React.ReactNode;
  descriptionClassName?: string;
  label?: React.ReactNode;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  error?: FieldError;
  disabled?: boolean;
  clearable?: boolean;
};

type FloatingInputProps = CommonInputProps & {
  labelPosition?: "floating";
  id?: string;
};

type StackedInputProps = CommonInputProps & {
  labelPosition: "stacked";
  id: string;
};

type InputProps = FloatingInputProps | StackedInputProps;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      description,
      descriptionClassName,
      leadingIcon,
      trailingIcon,
      error: errorMessage,
      placeholder: placeholderProp,
      disabled,
      labelPosition = "floating",
      id: idProp,
      value,
      defaultValue,
      onChange,
      readOnly,
      type = "text",
      clearable = true,
      ...rest
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
    const inputValue = value ?? uncontrolledValue ?? "";
    const canClearInput =
      clearable &&
      !disabled &&
      !readOnly &&
      ["text", "search", "email", "url", "tel", "password"].includes(type);
    const shouldShowClearButton =
      canClearInput && String(inputValue).length > 0;
    const placeholder =
      placeholderProp ?? (typeof label === "string" ? label : undefined);
    const inputId = labelPosition === "stacked" ? `input-${idProp}` : undefined;
    const afterText = extractFormErrorMessage(errorMessage) || description;
    const describedById = afterText ? `${inputId}-description` : undefined;

    // For stacked layouts, need to pass focus down to the input element on click, because label is separated
    const handleInputParentClick = useCallback<
      MouseEventHandler<HTMLDivElement>
    >((e) => {
      e.currentTarget.querySelector("input")?.focus();
    }, []);

    const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
      (event) => {
        if (value === undefined) {
          setUncontrolledValue(event.target.value);
        }
        onChange?.(event);
      },
      [onChange, value],
    );

    const handleClear = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (event) => {
        event.preventDefault();
        event.stopPropagation();

        const input = inputRef.current;
        if (!input) return;

        const valueSetter = Object.getOwnPropertyDescriptor(
          input,
          "value",
        )?.set;
        const prototype = Object.getPrototypeOf(input);
        const prototypeValueSetter = Object.getOwnPropertyDescriptor(
          prototype,
          "value",
        )?.set;

        if (prototypeValueSetter && valueSetter !== prototypeValueSetter) {
          prototypeValueSetter.call(input, "");
        } else {
          valueSetter?.call(input, "");
        }

        setUncontrolledValue("");
        input.dispatchEvent(new Event("input", { bubbles: true }));
        input.focus();
      },
      [],
    );

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const inputClassName = clsx(
      "placeholder:text-textSubtle text-textDefault peer max-w-full flex-1 outline-none",
      labelPosition !== "stacked" && "focus:placeholder-transparent",
      // type=search draws a native cancel control that stacks on the DS clear
      // button; hide it whenever we render our own.
      canClearInput &&
        "[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-cancel-button]:appearance-none",
    );

    const endAdornment = (
      <>
        {shouldShowClearButton && (
          // Match the text line height so toggling the clear button never
          // changes the input's height (the default md IconButton is taller
          // than the content box and would grow the row on first input).
          <IconButton
            aria-label="Clear input"
            onClick={handleClear}
            className="h-6 w-6"
          >
            <X size={16} />
          </IconButton>
        )}
        {trailingIcon}
      </>
    );

    const sharedContainerClassNames = twMerge(
      clsx(
        "bg-surface-default relative inline-flex cursor-text items-center gap-2 rounded-lg border border-borderDefault px-4 py-2 transition focus-within:border-interactive-selected focus-within:ring-2 focus-within:ring-inset focus-within:ring-interactive-selected",
        errorMessage &&
          "border-borderError focus-within:border-borderError focus-within:ring-borderError",
        className,
      ),
      disabled && "bg-surface-disabled text-textSubtle opacity-40",
      readOnly && "bg-surface-disabled text-textSubtle",
      !disabled && "hover:border-interactive-selected",
    );

    if (labelPosition === "stacked") {
      return (
        <div className="flex flex-col gap-2">
          {label ? (
            <label
              htmlFor={inputId}
              className={clsx(
                "text-label-sm",
                errorMessage ? "text-textError" : "text-textDefault",
              )}
            >
              {label}
            </label>
          ) : null}

          <div
            className={sharedContainerClassNames}
            onClick={handleInputParentClick}
          >
            {leadingIcon}

            <input
              type={type}
              className={inputClassName}
              placeholder={placeholder}
              disabled={disabled}
              readOnly={readOnly}
              value={value}
              defaultValue={defaultValue}
              onChange={handleChange}
              ref={inputRef}
              aria-invalid={!!errorMessage || undefined}
              aria-describedby={describedById}
              {...rest}
              id={inputId}
            />

            {endAdornment}
          </div>

          {afterText ? (
            <div
              id={describedById}
              className={twMerge(
                clsx(
                  "text-body-xs inline-block px-4",
                  errorMessage ? "text-textError" : "text-textSubtle",
                ),
                descriptionClassName,
              )}
            >
              {afterText}
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-1">
        <label className={sharedContainerClassNames}>
          {leadingIcon}

          <input
            type={type}
            className={inputClassName}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            value={value}
            defaultValue={defaultValue}
            onChange={handleChange}
            ref={inputRef}
            {...rest}
          />
          <span
            className={clsx(
              "peer-focus:text-interactive-selected bg-surface-default text-label-xs absolute top-0 left-4 -translate-y-1/2 px-1 opacity-100 transition peer-placeholder-shown:opacity-0 peer-focus:opacity-100",
              errorMessage ? "text-textError" : "text-textDefault",
            )}
          >
            {label}
          </span>

          {endAdornment}
        </label>
        {afterText && (
          <div
            className={twMerge(
              clsx(
                "text-body-xs inline-block px-4",
                errorMessage ? "text-textError" : "text-textSubtle",
              ),
              descriptionClassName,
            )}
          >
            {afterText}
          </div>
        )}
      </div>
    );
  },
);
